/**
 * Validate a server-supplied `target_url` before assigning to
 * `window.location.href`. Rejects:
 *  - `javascript:` / `data:` / `blob:` / `file:` and other non-http(s) schemes
 *    that would execute inline JS or otherwise sidestep navigation.
 *  - Absolute http(s) URLs whose origin doesn't match this window's —
 *    a server-supplied `https://evil.com/...` would otherwise become an
 *    open-redirect / phishing vector.
 *
 * Same-origin paths are normalised via `new URL` before the origin check
 * so backslash and protocol-relative tricks (`/\evil.com`, `//evil.com`)
 * can't slip through.
 */
export function isSafeNavigationUrl(raw: string): boolean {
  if (typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  if (!trimmed) return false;

  let parsed: URL;
  try {
    parsed = new URL(trimmed, window.location.origin);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return false;
  }

  // Same-origin only. The relative-path branch resolves against
  // `window.location.origin` so it lands here too; absolute URLs to a
  // different origin get rejected.
  return parsed.origin === window.location.origin;
}
